// SignalR client for the LiveCandles hub. Mirrors the patterns in
// TraderxAlerts.Application.Clients.Candles.LiveCandlesSubscriber:
// - automatic reconnect
// - replay subscription on reconnect (server has no per-connection memory)
// - canonical-string serialization (server emits "1m"/"perps" strings; the JS client
//   accepts these directly into the typed CandleDto since interval/marketType are
//   already string-literal types in our TypeScript model)
//
// Single `(coin, interval, marketType)` subscription per hook instance; if the user
// changes the strategy's coin/interval, the parent re-renders and the effect cleans
// up + reconnects under the new tuple.

import { useEffect, useRef, useState } from "react";
import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel,
} from "@microsoft/signalr";
import type { CandleDto, CandleInterval, MarketType } from "../api/types";
import { URLS } from "../api/client";

export interface LiveCandlesState {
  connected: boolean;
  lastCandle: CandleDto | null;
}

export function useLiveCandles(
  coin: string | null,
  interval: CandleInterval | null,
  marketType: MarketType | null,
  onCandleClosed: (candle: CandleDto) => void,
): LiveCandlesState {
  const [connected, setConnected] = useState(false);
  const [lastCandle, setLastCandle] = useState<CandleDto | null>(null);
  // Latest callback ref so the effect doesn't tear down on every parent render.
  const cbRef = useRef(onCandleClosed);
  cbRef.current = onCandleClosed;

  useEffect(() => {
    if (!coin || !interval || !marketType) return;

    let cancelled = false;
    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${URLS.liveCandles}/hubs/live-candles`)
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    // Server pushes via ILiveCandlesHubClient.OnCandleClosed.
    connection.on("OnCandleClosed", (candle: CandleDto) => {
      if (cancelled) return;
      setLastCandle(candle);
      cbRef.current(candle);
    });

    connection.onreconnected(async () => {
      try {
        await connection.invoke<boolean>("SubscribeToCandle", coin, interval, marketType);
      } catch {
        // already subscribed or transient — auto-reconnect will retry on next disconnect
      }
    });

    (async () => {
      try {
        await connection.start();
        if (cancelled) return;
        await connection.invoke<boolean>("SubscribeToCandle", coin, interval, marketType);
        if (!cancelled) setConnected(true);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn("LiveCandles connection failed:", err);
      }
    })();

    return () => {
      cancelled = true;
      setConnected(false);
      // Best-effort unsubscribe — fire-and-forget on stop
      if (connection.state === HubConnectionState.Connected) {
        connection
          .invoke("UnsubscribeFromCandle", coin, interval, marketType)
          .catch(() => undefined)
          .finally(() => connection.stop().catch(() => undefined));
      } else {
        connection.stop().catch(() => undefined);
      }
    };
  }, [coin, interval, marketType]);

  return { connected, lastCandle };
}
